"use client";

import * as stylex from "@stylexjs/stylex";
import { CalendarPlus, ChevronLeft, ChevronRight } from "lucide-react";
import { useRef } from "react";
import { copy } from "@/copy";
import type { WeatherIcon } from "@/domain/schemas";
import type { Dot } from "@/domain/week";
import "@/theme/tokens.stylex";
import { colors, motion, radii } from "@/theme/tokens.stylex";
import "@/theme/type.stylex";
import { type } from "@/theme/type.stylex";
import { Button } from "@/ui/Button";
import { Icon } from "@/ui/Icon";
import { IconButton } from "@/ui/IconButton";
import { PersonDot } from "@/ui/PersonDot";
import { press } from "@/ui/press";
import { useBoxSize } from "@/ui/useBoxSize";
import { weatherIcons } from "@/ui/weatherIcons";
export type WeekDay = {
  key: string;
  weekday: string;
  date: number;
  name: string;
  count: string | undefined;
  dots: readonly Dot[];
  forecast?: {
    icon: WeatherIcon;
    label: string;
    high: string;
  };
};
type WeekStripProps = {
  range: string;
  month: string;
  label: string;
  days: readonly WeekDay[];
  selected: string;
  today: string;
  wide: boolean;
  previousLabel: string;
  nextLabel: string;
  onPrevious: () => void;
  onNext: () => void;
  onSelect: (day: string) => void;
  onAddEvent: () => void;
};
type CellSize = {
  circle: number;
  count: boolean;
  weather: boolean;
};
const DAYS = 7;
const GAP = 2;
const COUNT_CELL = 120;
const WEATHER_CELL = 56;
const CIRCLE = {
  large: 56,
  small: 48
} as const;
const _temp = {
  kmuXW: "x2lah0s",
  k1xSpc: "xrvj5dj",
  kgQiWS: "x1ku5rj1",
  kaIpWk: "xqhrb7l",
  kGuDYH: "xneq65o",
  k63SB2: "x1cf7cre",
  kb6lSQ: "x1roq8pg",
  kLWn49: "xo5v014",
  kcqcaj: "xss6m8b",
  k1ekBW: "xs2xxs2",
  kIyJzY: "x16c7our",
  kAMwcw: "xwji4o3",
  "$$css": true
};
const styles = {
  day: {
    k7Eaqz: "xeuugli",
    kVQacm: "xb3r6kr",
    k1xSpc: "x78zum5",
    kXwgrk: "xdt5ytf",
    kGNEyG: "x6s0dn4",
    kOIVth: "x1jnr06f",
    kB7OPa: "x9f619",
    k8WAf4: "x1vvogim",
    kg3NbH: "xfn1xvj",
    kMzoRj: "xc342km",
    kaIpWk: "xgqmno8",
    kWkggS: "xjbqb8w",
    kMwMTN: "x1a11ngh",
    kVVagm: "xln7xf2",
    kkrTdU: "x1ypdohk",
    k1ekBW: "x11xpdln",
    kIyJzY: "x1bpocu2",
    kAMwcw: "x1em5f5i",
    $$css: true
  },
  circle: (size: number) => [_temp, {
    kzqmXN: size != null ? "x5lhr3w" : size,
    kZKoxP: size != null ? "x16ye13r" : size,
    $$css: true
  }, {
    "--x-width": (val => typeof val === "number" ? val + "px" : val != null ? val : undefined)(size),
    "--x-height": (val => typeof val === "number" ? val + "px" : val != null ? val : undefined)(size)
  }],
  selected: {
    kWkggS: "xfenp3h",
    kMwMTN: "xnnvkky",
    $$css: true
  },
  today: {
    kWkggS: "x5r8ks9",
    $$css: true
  },
  add: {
    kAzted: "xbktkl8",
    k8WAf4: "xt970qd",
    kZCmMZ: "x106a9eq",
    kwRFfy: "x64bnmy",
    kWkggS: "x5r8ks9",
    $$css: true
  }
};
export function WeekStrip({
  range,
  month,
  label,
  days,
  selected,
  today,
  wide,
  previousLabel,
  nextLabel,
  onPrevious,
  onNext,
  onSelect,
  onAddEvent
}: WeekStripProps) {
  const grid = useRef<HTMLFieldSetElement>(null);
  const size = cellSize(useBoxSize(grid)?.width ?? 0);
  return <div className="x2lah0s x78zum5 x6s0dn4 x167g77z xeuugli xb3r6kr x9f619 x12ulsxz xaope02 xwe7p7i xh540kj x1a11ngh">
      <div className="x2lah0s x1f2tiqu x78zum5 xdt5ytf x1jnr06f xdqyycr x9f619">
        <span className="xr0iwhn xif65rj xbzybt3 x1u7k74 xtvhhri">{range}</span>
        <span className="xneq65o x1v94iar xrg9stw x1159mfc">{month}</span>
      </div>
      <IconButton icon={ChevronLeft} label={previousLabel} size="touch" onClick={onPrevious} />
      <fieldset ref={grid} aria-label={label} className="x1ghz6dp x1717udv xc342km x1iyjqo2 x1r8uery xrvj5dj x1gj6vdr x195vfkc xeuugli">
        {days.map(day => <DayCell key={day.key} day={day} selected={day.key === selected} today={day.key === today} size={size} onClick={() => onSelect(day.key)} />)}
      </fieldset>
      <IconButton icon={ChevronRight} label={nextLabel} size="touch" onClick={onNext} />
      <div className="x2lah0s x78zum5 x6s0dn4 x167g77z xmzvs34">
        {wide ? <Button variant="ghost" icon={CalendarPlus} onClick={onAddEvent} xstyle={styles.add}>
            {copy.calendar.addEvent}
          </Button> : <IconButton icon={CalendarPlus} label={copy.calendar.addEvent} size="touch" onClick={onAddEvent} />}
      </div>
    </div>;
}
type DayCellProps = {
  day: WeekDay;
  selected: boolean;
  today: boolean;
  size: CellSize;
  onClick: () => void;
};
function DayCell({
  day,
  selected,
  today,
  size,
  onClick
}: DayCellProps) {
  return <button type="button" aria-label={day.name} aria-pressed={selected} aria-current={today ? "date" : undefined} onClick={onClick} {...stylex.props(styles.day, press.scale)}>
      <span className="xr0iwhn x4z9k3i xbzybt3 x1d3mw78 xtvhhri">{day.weekday}</span>
      <span {...stylex.props(styles.circle(size.circle), today && !selected && styles.today, selected && styles.selected)}>
        {day.date}
      </span>
      <span className="x78zum5 x1a02dak xl56j7k x6s0dn4 x1dbijih xtqikln xvrgn94 x193iq5w">
        {size.count && day.count && <span className="xr0iwhn x4z9k3i xbzybt3 xd4r4e8 xtvhhri xuxw1ft">{day.count}</span>}
        {!size.count && day.dots.length > 0 && <span className="x78zum5 x6s0dn4 x1ed6fcf xmix8c7">
            {day.dots.map(dot => <span key={dot.id} data-testid="week-dot" className="x78zum5">
                <PersonDot color={dot.color} size="xsm" />
              </span>)}
          </span>}
        {size.weather && day.forecast && <span className="x78zum5 x6s0dn4 x1jnr06f xd4r4e8 xuxw1ft x1jvydc1 x1cf7cre xss6m8b">
            <Icon icon={weatherIcons[day.forecast.icon]} size={18} strokeWidth={1.75} label={day.forecast.label} />
            {day.forecast.high}
          </span>}
      </span>
    </button>;
}
function cellSize(gridWidth: number): CellSize {
  const cell = (gridWidth - (DAYS - 1) * GAP) / DAYS;
  return {
    circle: cell >= COUNT_CELL ? CIRCLE.large : CIRCLE.small,
    count: cell >= COUNT_CELL,
    weather: cell >= WEATHER_CELL
  };
}